import React, { useState, useContext, useRef, useEffect } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { AppDataContext, VLink } from "../AppDataContext"
import useNetwork from '../../useNetwork';
import { Helmet } from "react-helmet";
import useInfiniteScroll from "../../useInfiniteScroll";
import { LotTile } from '../auction/LotTile';
import { BidDialog } from '../auction/BidDialog';
import { Modal } from 'bootstrap';
import { urlEncodeText, computeSystemTimeDifference, getSecondsToTime, toGMT } from '../../helpers';
import { Loader } from '../Loader';
import CurrencyFormat from 'react-currency-format';
import { ConfirmDialog } from '../ConfirmDialog';

export function Favorites(props) {

	const context = useContext(AppDataContext);
	const { loading, networkError, netRequest, netRenderError } = useNetwork(true);
	const history = useHistory();

 
	const [currentTitle, setCurrentTitle] = useState(null);

	const startIx = useRef(0);
	// because the way that BS columns are working, we should have page sizes of multiples of 8, so we don't get
	// breaks in the middle of a row (it will normally show only 4 or 2 per row, so multiples of 4 would probably work, too)
	const pageSize = 8;


	const [isFetching, setIsFetching, isDone, setIsDone] = useInfiniteScroll(fetchLots);
	const [endReached, setEndReached] = useState(false);
	const [totalCount, setTotalCount] = useState(null);
	const totalCountRef = useRef(null);

	// because we need to reference the lot list in a callback from the SignalR Hub, we can't see the
	// state held value (it will only be its initial value and never change).  So we have to store it
	// in a ref, which we will set and synchronize with state at the same time
	const lotsRef = useRef([]);
	const [lots, _setLots] = useState([]);
	const setLots = (lots) => {
		lotsRef.current = lots;
		_setLots(lots);
	}
	const listeningForLotsRef = useRef([]);

	const [systemSecondsToCloseDelta, setSystemSecondsToCloseDelta] = useState(0);

	const newBidPlaced = (bid, isBroadcast) => {
		// locate the lot for the LotTile we want to tell to update with a bid broadcast or as the result of a Place Bid
		// note that since we're in a callback in the Broadcast, we don't have access to the current lots state variable,
		// so we have to go to the Ref.  but to get state to update, we'll have to put the current into a NEW array
		let newLots = [...lotsRef.current];
		for (let ix = 0; ix < newLots.length; ix++) {
			if (newLots[ix].secureId === bid.lotSecureId) {
				if (bid.bidResultEnum == "LotWidthdrawn" || bid.bidResultEnum == "LotAdded") {
					// special case, not really a bid, but using the bid broadcast to notify anyone watching that
					// the lot has been widthdrawn from or added to the auction
					console.log('Lot ' + bid.lotSecureId + ': ' + bid.bidResultEnum);
					fetchLots(true);
				} else if (isBroadcast && bid.paddleNumber == newLots[ix].auctionSession.auction.paddleNumber) {
					// if the bid paddle number from a broadcast coming in is the same as this user's paddle number, then
					// we just got a broadcast from a different tab/browser, or registration is complete, and the pending bids are coming in...
					// so the easiest thing to do us update the view
					console.log('Self Bid from either other tab or Pending Queue');
					fetchLots(true);
				} else if (bid.bidResultEnum == "NotApproved") {
					newLots[ix].pendingBidAmount = bid.pendingBidAmount;
				} else {
					// update the close time delta, as it might be different
					const localSystemSecondsToCloseDelta = computeSystemTimeDifference(bid.closeTime, bid.secondsToClose, new Date());
					setSystemSecondsToCloseDelta(localSystemSecondsToCloseDelta);

					bid.isBroadcast = isBroadcast;
					newLots[ix].currentBid = bid;
				}
				break;
			}
		}
		setLots(newLots);
	};

	const newBidBroadcastRef = useRef(null);
	const newBidBroadcast = (event) => {
		newBidPlaced(event.detail, true);
	};

	const [triggerFirstFetch, setTriggerFirstFetch] = useState(null);
	const backParameter = "backToFavorites";

	// setup the scroll position so if back is pressed, we will scroll back to the current location
	const lotTileLinkClicked = () => {
		// because of the way we do Routing with this component, the only prop we get is the tab variable, so it appears
		// we can't store the scroll position in state, as we do on the auction page, because it gets cleared when we come back
		// and only the tab prop is there.
		history.replace({ search: "?" + backParameter + "=" + document.documentElement.scrollTop + "," + startIx.current });
	}

	useEffect(() => {
		if (triggerFirstFetch) {
			let scrollEvent = null;

			const backParm = new URLSearchParams(window.location.search).get(backParameter);
			if (backParm != null) {
				const backParms = backParm.split(",");
				scrollEvent = { scroll: Number(backParms[0]), startIx: Number(backParms[1]) }

				// hide that ugly parm
				history.replace({ search: null });
			}

			fetchLots(true, scrollEvent);
		}
	}, [triggerFirstFetch]);

	useEffect(() => {
		if (props.tab) {
			if (props.tab == "favorites") {
				setCurrentTitle("Favorites");
			} else if (props.tab == "pastfavorites") {
				setCurrentTitle("Past Favorites");
			} else if (props.tab == "currentbids") {
				setCurrentTitle("Current Bids");
			} else if (props.tab == "pastbids") {
				setCurrentTitle("Past Bids");
			} else if (props.tab == "pastitems") {
				setCurrentTitle("Past Items");
			}

			startIx.current = 0;
			totalCountRef.current = null;
			setTotalCount(null);
			setLots([]);
			setTriggerFirstFetch(Math.random());
		}
	}, [props.tab]);

	useEffect(() => {
		setTriggerFirstFetch(Math.random());
	}, [context.registeredAuctionsChanged]);

	// this useEffect has [] as the parms, so only gets call on didMount
	useEffect(() => {

		// kick off the countdown timer
		tick();

		// clean up if this effect gets called again
		return () => {
			// remove timer
			if (timer.current) {
				console.log('clearing timer');
				clearTimeout(timer.current);
			}
			if (fetchTimer.current != null) {
				console.log("Timer Fetch Canceled");
				clearTimeout(fetchTimer.current);
			}
		};
	}, []);

	useEffect(() => {
		if (lots) {
			if (context.hubConnected) {
				newBidBroadcastRef.current = newBidBroadcast;
				lots.forEach((l, ix) => {
					if (l.secondsToClose > 0 &&
						listeningForLotsRef.current.indexOf(l.secureId) == -1) {
						listeningForLotsRef.current.push(l.secureId);
						context.subscribeToBids(l.secureId, newBidBroadcastRef.current);
					}
				});
			} else {
				console.log("Hub not up yet, subscribing later")
			}
		}
		// clean up if this effect gets called again
		return () => {
			// remove subscriptions for any lots were asked to listen for
			listeningForLotsRef.current.forEach((l, ix) => {
				context.unsubscribeToBids(l, newBidBroadcastRef.current);
			});
			listeningForLotsRef.current = [];
		};
	}, [lots]);

	useEffect(() => {
		if (context.hubConnected && lots.length > 0) {
			if (newBidBroadcastRef.current == null) {
				newBidBroadcastRef.current = newBidBroadcast;
			}
			lots.forEach((l, ix) => {
				if (listeningForLotsRef.current.indexOf(l.secureId) == -1) {
					listeningForLotsRef.current.push(l.secureId);
					context.subscribeToBids(l.secureId, newBidBroadcastRef.current);
				}
			});
			console.log("Hub Connected, fetching current lots");
			fetchLots(true);
		}
		if (context.hubConnected == false && newBidBroadcastRef.current != null) {
			// remove subscriptions for any lots were asked to listen for
			listeningForLotsRef.current.forEach((l, ix) => {
				context.unsubscribeToBids(l, newBidBroadcastRef.current);
			});
			listeningForLotsRef.current = [];
			newBidBroadcastRef.current = null;
		}
	}, [context.hubConnected]);

	const fetchTimer = useRef(null);

	function fetchLots(fromBeginning, scrollEvent) {

		const getNextLots = async (startingAt, numberToFetch, scrollPosition) => {
			if (totalCountRef.current == null || startingAt < totalCountRef.current) {
				let itemsToFetch = pageSize;
				if (numberToFetch != null) {
					itemsToFetch = numberToFetch;
				}
				const url = 'api/site/' + props.tab + '/' + startingAt + '/' + itemsToFetch;
				console.log(url);
				const response = await netRequest(url);

				if (response.data != null) {
					if (response.data.lots.length > 0) {
						// update the close time delta, as it might be different
						const firstLot = response.data.lots[0];
						setSystemSecondsToCloseDelta(computeSystemTimeDifference(firstLot.closeTime, firstLot.secondsToClose, new Date()));
					}

					totalCountRef.current = response.data.totalCount;
					setTotalCount(response.data.totalCount);

					// set our next starting point
					startIx.current = startingAt + itemsToFetch;

					const endReached = startIx.current >= response.data.totalCount;
					setIsDone(endReached);
					setEndReached(endReached);


					// assign the paddle numbers for each auction, if set
					response.data.lots.forEach((lot, ix) => {
						if (lot.auctionSession.auction.isRegistered) {
							context.setRegisteredAuction(lot.auctionSession.auction.secureId, lot.auctionSession.auction.paddleNumber);
						}
					});

					if (startingAt == 0)
						setLots(response.data.lots);
					else
						setLots([...lots, ...response.data.lots]);

					if (scrollPosition != null) {
						window.scrollTo(0, scrollPosition);
					}
				}
			} else {
				setEndReached(true);
			}
			setIsFetching(false);
		};

		// if the infinite scroll calls us, the first param is false
		let startingAt = fromBeginning === true ? 0 : startIx.current;
		let itemsToFetch = pageSize;
		let scrollTo = null;
		if (fromBeginning === true) {
			if (scrollEvent) {
				// when the back link is noted, these vars are passed in, so fetch the set amount and scroll
				itemsToFetch = scrollEvent.startIx;
				scrollTo = scrollEvent.scroll;
			} else if (startIx.current !== 0) {
				// any other time we ask to fetch from the beginning, we want to pull everything so far
				itemsToFetch = startIx.current;
			}
		}

		// if they scroll while the timer is running, then kill the timer
		if (fetchTimer.current != null) {
			clearTimeout(fetchTimer.current);
			fetchTimer.current = null;
			console.log("Timer Fetch Canceled");
		}

		getNextLots(startingAt, itemsToFetch, scrollTo);
	};

	const notifyClosed = (lot) => {
		if (endReached === false) {
			fetchTimer.current = setTimeout(() => {
				fetchTimer.current = null;
				// fetch whatever amount they were looking at so far
				fetchLots(true);
			}, 4000);
		}
	};

	// opening a dialog is a bit of an effort.  We have a state var, openBidDialog
	// so that we can set the lot before we enable the dialog.  since the lot is null
	// when the page loads, we don't even have a dialog on the page.  once set
	// it will generate the dialog.  then we set the state to open the dialog so now
	// the useEffect watching openBidDialog will actually find the modal in the dom
	// i use a random number for the reinitialize property on the dialog component
	// so that we can change it, and the dialog will reset any state vars before opening
	const [selectedLot, setSelectedLot] = useState(null);
	const [openBidDialog, setOpenBidDialog] = useState(false);
	const [reinitializeBidDialog, setReinitializeBidDialog] = useState(Math.random());

	const showBidDialog = async (lot) => {
		setSelectedLot(lot);
		setOpenBidDialog(true);
	}

	useEffect(() => {
		if (openBidDialog) {
			setReinitializeBidDialog(Math.random());
			var myModal = new Modal("#placeBidModal", {});
			myModal.show();
		}
		setOpenBidDialog(false);
	}, [openBidDialog]);


	// state variable which is used to broadcast tick events to the tiles
	const [gmt, setGmt] = useState(new Date());
	const timer = useRef(null);

	const tick = () => {
		// set up timer
		timer.current = setTimeout(() => {
			setGmt(new Date());
			tick();
		}, 1000);
	}
	const [selectedSecondsToCLose, setSelectedSecondsToCLose] = useState(null);

	useEffect(() => {
		if (selectedLot && gmt) {
			setSelectedSecondsToCLose(getSecondsToTime(selectedLot.closeTime, gmt, systemSecondsToCloseDelta));
		}
	}, [gmt, selectedLot, systemSecondsToCloseDelta]);	

	const [bidToRetract, setBidToRetract] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const openRetractDialog = (bidToRetractId) => {
		setBidToRetract(bidToRetractId);
		setErrorMessage(null);
	}
	const modalRetractOkClicked = async () => {
		setErrorMessage(null);

		const url = 'api/site/retractBid';

		const resp = await netRequest(url, { Param: bidToRetract });

		if (resp.isError) {
			setErrorMessage("An error occurred.  Please try again.  If this continues, close and reopen your browser. (code: " + resp.status + ")");
		} else {
			if (resp.data != null)
				setErrorMessage(resp.data);
			else {
				Modal.getInstance("#retractBidConfirmModal").hide();
				fetchLots(true);
			}
		}

		return false;
	};

	return (
		<>
			<Helmet>
				<title>{currentTitle}</title>
			</Helmet>

			<ConfirmDialog cancelText="No" okText="Yes"
				okClicked={modalRetractOkClicked}
				id="retractBidConfirmModal"
				headerText="Confirm Bid Retraction"
				modalSize='modal-md'
			>
				{errorMessage &&
					<div class="col-12 mb-3">
						<div class="alert alert-danger">{errorMessage}</div>
					</div>
				}
				Are you sure you want to retract this bid?

			</ConfirmDialog>

			<div class="tab-pane container h-100 py-3 px-0 active">
				{!props.isMyAccount ?
					<>

						{props.tab == "currentbids" &&
							<div class="btn-group mb-3">
								<button class="btn btn-primary active">Upcoming</button>
								<VLink to="/dashboard/pastitems" class="btn btn-secondary">Past</VLink>
							</div>
						}
						{props.tab == "pastitems" &&
							<div class="btn-group mb-3">
								<VLink to="/dashboard/currentbids" class="btn btn-secondary">Upcoming</VLink>
								<button class="btn btn-primary active">Past</button>
							</div>
						}

						{props.tab == "favorites" &&
							<div class="btn-group mb-3">
								<button class="btn btn-primary active">Upcoming</button>
								<VLink to="/dashboard/pastfavorites" class="btn btn-secondary">Past</VLink>
							</div>
						}
						{props.tab == "pastfavorites" &&
							<div class="btn-group mb-3">
								<VLink to="/dashboard" class="btn btn-secondary">Upcoming</VLink>
								<button class="btn btn-primary active">Past</button>
							</div>
						}
					</>
					:
					<ul class="nav nav-tabs nav-justified nav-tabs-adjusted">

						<li class="nav-item">
							<VLink to="/account/bids/currentbids" className={"nav-link " + (props.tab == "currentbids" ? "active" : "")}>Current Bids</VLink>
						</li>
						<li class="nav-item">
							<VLink to="/account/bids/pastbids" className={"nav-link " + (props.tab == "pastbids" ? "active" : "")}>Past Bids</VLink>
						</li>

					</ul>

				}
                <div class="row">
					{!props.isMyAccount ?
                        lots.map((l) => (
                            <LotTile
                                auctionId={l.auctionSession.secureId}
                                lot={l}
                                currentBid={l?.currentBid}
                                key={l.secureId}
								showClosed={props.tab != "favorites" && props.tab != "currentbids"}                                             
                                gmt={gmt}
								systemSecondsToCloseDelta={systemSecondsToCloseDelta}
								showAuctionInfo={true}
                                notifyClosed={notifyClosed}
                                onPlaceBid={showBidDialog}
								onLinkClicked={lotTileLinkClicked}
								isFavorite={props.tab == 'favorites' || props.tab == 'pastfavorites' }
							/>

						))
						:
                        <>
                            <Loader loading={loading} />
                            <div class="col">
                                <div class="table-responsive">
                                    <table class="table table-striped align-middle table-borderless">
                                        <thead>
                                            <tr>
                                                <th scope="col"></th>
                                                <th scope="col">Lot #</th>
                                                <th scope="col">Title</th>
                                                <th scope="col">Auction</th>
                                                <th scope="col">Current Price</th>
                                                <th scope="col">Status</th>
                                                {props.tab == "currentbids" && <th scope="col"></th>}
                                            </tr>
										</thead>
										<tbody>
                                        {lots.map((l) =>
                                                       
                                                <tr>
                                                    <td>
                                                        {l.inventoryItemImageSecureId == null ?
                                                            <img src="images/noimage.png" title="" alt="" height="30" />
                                                            :
														<img src={"/api/site/inventoryimage/" + l.inventoryItemImageSecureId + "/30"} title={l.title} alt={l.title} />
													}
												</td>
												<td align="right">{l.lotNumber}</td>
												<td>
													<VLink to={'/auction/' + l.auctionSession.secureId + '/lot/' + l.secureId + '/' + urlEncodeText(l.title)}
														style={{ textDecoration: 'none' }}
														class="text-uppercase"
														onClick={lotTileLinkClicked}
														title={l.title}>
														{l.title.toUpperCase()}
													</VLink>
												</td>
												<td>
													<VLink to={"/auction/" + l.auctionSession.secureId + '/' + urlEncodeText(l.auctionSession?.title)}
														title={l.auctionSession?.title}
														onClick={lotTileLinkClicked}
														class="text-uppercase"
														style={{ textDecoration: 'none' }}>
														{l.auctionSession?.title}
													</VLink>
												</td>
												<td align="right">
													<CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={l.currentBid?.currentAmount ?? l.startPrice} displayType={'text'} thousandSeparator={true} prefix={'$'} />
												</td>
												<td align="center">
													{/* you can have a bid that you retracted, so you have a bid, just not any outstanding bids. */}
													{l.hasBid == false ?
															<label class="me-1 mb-1 auction-label outbidded">Retracted</label>
														: l.currentBid.isThisUser ?
															<label class="me-1 mb-1 auction-label highest">
																{l.secondsToClose < 0 ? "Won!" : "High Bid"}
															</label>
														:
															<label class="me-1 mb-1 auction-label outbidded">Outbid</label>
													}
												</td>
												{props.tab == "currentbids" &&
													<td>
													{(l.currentBid?.isThisUser &&
														// you can only retract a bid 24 hours prior to auction closing
														new Date(toGMT(l.auctionSession.closeTime).getTime() - (1000 * 60 * 60 * 24)) > gmt) &&
															<button type="button" class="btn btn-sm btn-link" data-bs-toggle="modal"
																data-bs-target="#retractBidConfirmModal"
																onClick={() => openRetractDialog(l.currentBid.bidSecureId)}>
																Retract Bid
															</button>
													}
													</td>
												}
											</tr>
													
											)}
										</tbody>
								</table>

							</div>
							</div>
						</>
                    }
                    {isFetching && !isDone && <div>Fetching More...</div>}
					{(totalCount > 0 && endReached) && <div class="col-12"><p class="text-center end-of-auction">End of {currentTitle}</p></div>}
					{(!isFetching && totalCount > 0 && !endReached) &&
						<div class="col-12 mb-3 text-end">

						<button onClick={fetchLots} class="btn btn-secondary" title="Show More...">
							Show More <i class="bi bi-arrow-right-short"></i>
							</button>
							</div>
					}
					{totalCount == 0 && <div class="col-12"><p class="text-center end-of-auction">No {currentTitle}</p></div>}
                </div>

            </div>



			{selectedLot &&
				<BidDialog
				id="placeBidModal"
				lot={selectedLot}
				onBidPlaced={newBidPlaced}
				showConfirm={false}
				reinitialize={reinitializeBidDialog}
				secondsToClose={selectedSecondsToCLose}
			/>
			}
		</>
    );

}


export function BiddingHistoryLayout(props) {
	return (
		<div class="row mt-3">
			<div class="col-12 mb-1">
				<h4 class="auction-grid-header">
					Bidding History
					<div class="divider mt-2"></div>
				</h4>
			</div>
			<div class="col-12 mt-3">
				{props.children}
			</div>
		</div>
	)
}
